import { useState } from 'react'
import { Alert, Button, Form, Input, Modal, Select } from 'antd'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, getFriendlyErrorMessage } from '@kolab/dms-base/lib'
import {
  createLicenseProduct,
  listLicenseProducts,
  updateLicenseProduct,
} from '../api/licenseProduct.api'
import { licenseProductKeys } from '../api/queryKeys'
import type { LicenseProduct, LicenseProductStatus } from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { Can, P } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'


type Mode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; product: LicenseProduct }

export default function LicenseProductsPage() {
  const queryClient = useQueryClient()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<LicenseProduct>({
    queryKey: licenseProductKeys.list({ pageSize: 20 }),
    queryFn: (params) => listLicenseProducts(params),
    pageSize: 20,
  })

  const [modal, setModal] = useState<Mode>({ kind: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  function openModal(mode: Mode) {
    setFormError(null)
    setModal(mode)
  }

  const createMut = useMutation({
    mutationFn: createLicenseProduct,
    onSuccess: () => {
      toast.success('License product created')
      queryClient.invalidateQueries({ queryKey: licenseProductKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => {
      handleFormError(error, 'Failed to create license product')
    },
  })

  const updateMut = useMutation({
    mutationFn: ({
      id,
      name,
      description,
      status,
    }: { id: string; name: string; description?: string; status: LicenseProductStatus }) =>
      updateLicenseProduct(id, { name, description, status }),
    onSuccess: () => {
      toast.success('License product updated')
      queryClient.invalidateQueries({ queryKey: licenseProductKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => {
      handleFormError(error, 'Failed to update license product')
    },
  })

  const columns: ColumnsType<LicenseProduct> = [
    { title: 'Code', dataIndex: 'code', key: 'code' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Can permission={P.license_catalog.product.update}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal({ kind: 'edit', product: record })}>
            Edit
          </Button>
        </Can>
      ),
    },
  ]

  return (
    <PageLayout
      actions={
        <Can permission={P.license_catalog.product.create}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal({ kind: 'create' })}>
            New product
          </Button>
        </Can>
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<LicenseProduct>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <ProductFormModal
        mode={modal}
        onCancel={() => setModal({ kind: 'closed' })}
        onSubmit={(payload) => {
          if (modal.kind === 'create') {
            createMut.mutate({ code: payload.code, name: payload.name, description: payload.description })
          } else if (modal.kind === 'edit') {
            updateMut.mutate({
              id: modal.product.id,
              name: payload.name,
              description: payload.description,
              status: payload.status,
            })
          }
        }}
        submitting={createMut.isPending || updateMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

interface FormFields {
  code: string
  name: string
  description?: string
  status: LicenseProductStatus
}

function ProductFormModal({
  mode,
  onCancel,
  onSubmit,
  submitting,
  error,
  onClearError,
}: {
  mode: Mode
  onCancel: () => void
  onSubmit: (fields: FormFields) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm<FormFields>()
  const isEdit = mode.kind === 'edit'
  const initial: FormFields =
    mode.kind === 'edit'
      ? {
          code: mode.product.code,
          name: mode.product.name,
          description: mode.product.description,
          status: mode.product.status,
        }
      : { code: '', name: '', description: '', status: 'ACTIVE' }

  return (
    <Modal
      open={mode.kind !== 'closed'}
      title={isEdit ? 'Edit license product' : 'New license product'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form form={form} layout="vertical" preserve={false} initialValues={initial} onFinish={onSubmit}>
        <Form.Item name="code" label="Code" rules={[{ required: true }, { max: 200, message: 'Code must not exceed 200 characters' }]}>
          <Input disabled={isEdit} placeholder="e.g. DP-PRO" />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }, { max: 200, message: 'Name must not exceed 200 characters' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description" rules={[{ max: 500, message: 'Description must not exceed 500 characters' }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        {isEdit && (
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'ACTIVE', label: 'ACTIVE' },
                { value: 'INACTIVE', label: 'INACTIVE' },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

export { LicenseProductsPage }
